import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { parseStoredUser } from "../../utils/moduleAccess";
import { Spinner } from "react-bootstrap";
import { ModalFrame } from "../Sales/SalesShared";
import {
    FiSearch, FiRefreshCw, FiPlus, FiChevronDown,
    FiArrowUp, FiArrowDown,
    FiTrash2, FiX, FiAlertTriangle,
    FiHash, FiChevronLeft, FiChevronRight,
    FiChevronsLeft, FiChevronsRight,
    FiUsers, FiUserCheck, FiUserX,
    FiMail, FiPhone, FiMapPin, FiTag, FiEdit3,
    FiCalendar, FiHome, FiDollarSign, FiUser, FiFileText,
    FiGlobe
} from "react-icons/fi"
import "../../Assets/Accounting.css"

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   1. CONSTANTS & TOKENS
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

const T = {
    radius: "12px",
    radiusSm: "8px",
    border: "#cbd5e1",
    bg: "#f7f9fc",
    cardBg: "#ffffff",
    textPrimary: "#0f172a",
    textMuted: "#64748b",
    textLight: "#94a3b8",
    accent: "#2563eb",
    success: "#059669",
    danger: "#dc2626",
    shadow: "0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)",
};

const SEARCH_TYPE_LABELS = {
    all: "All",
    id: "ID",
    contact: "Contact Person",
    name: "Company Name",
    email: "Email",
    phone: "Phone Number",
    country: "Country",
};

const ALL_SEARCH_TYPES = ["all", "id", "contact", "name", "email", "phone", "country"];

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   2. CUSTOM HOOKS
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

function useDebounce(value, delay = 350) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   3. SHARED SUB-COMPONENTS
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

/** 
 * Summary stats at the top of the page 
 */
const StatCard = ({ icon: Icon, color, label, value }) => {
    return (
        <div style={{
            background: T.cardBg, border: `1px solid ${T.border}`,
            borderRadius: T.radius, padding: "14px 18px",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)", display: "flex", alignItems: "center", gap: 14,
        }}>
            <div style={{ width: 40, height: 40, borderRadius: "10px", color, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon size={20} />
            </div>
            <div>
                <div style={{ fontSize: 12, color: T.textLight, marginBottom: 1 }}>{label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: T.textPrimary, lineHeight: 1 }}>{value ?? "—"}</div>
            </div>
        </div>
    );
};

const StatusBadge = ({ value = "Active" }) => {
    const isActive = String(value).toLowerCase() === "active";
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            background: isActive ? "#ecfdf5" : "#fef2f2",
            color: isActive ? T.success : T.danger,
            border: `1px solid ${isActive ? "#a7f3d0" : "#fecaca"}`,
            borderRadius: 999, padding: "2px 9px", fontSize: 11, fontWeight: 600,
        }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: isActive ? T.success : T.danger }} />
            {isActive ? "Active" : "Inactive"}
        </span>
    );
};

const Avatar = ({ name, size = 40 }) => {
    const char = (name?.trim()?.[0] ?? "?").toUpperCase();
    return (
        <div style={{
            width: size, height: size, borderRadius: "50%",
            background: "#eef2f7", border: `2px solid ${T.border}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0, fontWeight: 700, fontSize: size * 0.38, color: "#4f6a8a",
        }}>{char}</div>
    );
};

const SortTh = ({ label, id, sortBy, sortDir, onSort }) => {
    const active = sortBy === id;
    return (
        <th
            className="cl-th-sortable"
            aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
        >
            <button
                type="button"
                onClick={() => onSort(id)}
                aria-label={`Sort by ${label}`}
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    width: "100%",
                    padding: 0,
                    border: 0,
                    background: "transparent",
                    color: "inherit",
                    font: "inherit",
                    cursor: "pointer",
                    textAlign: "left",
                }}
            >
                {label}
                <span className={`cl-sort-icon${active ? " active" : ""}`} aria-hidden="true">
                    {active ? (sortDir === "asc" ? <FiArrowUp size={12} /> : <FiArrowDown size={12} />) : <span style={{ fontSize: 11 }}>⇅</span>}
                </span>
            </button>
        </th>
    );
};

const ContactRow = React.memo(({ row, index, isSelected, onRowClick }) => (
    <tr
        className={`cl-table-row${isSelected ? " selected" : ""}`}
        onClick={() => onRowClick(row)}
        onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onRowClick(row);
            }
        }}
        tabIndex={0}
        aria-selected={isSelected}
        title="Click to preview"
    >
        <td style={{ color: T.textLight, fontSize: 14 }}>{index + 1}</td>
        <td style={{ color: T.textMuted, fontSize: 13 }}>{row.id}</td>
        <td className="cl-td-strong" style={{ fontSize: 15 }}>{row.attention || "—"}</td>
        <td style={{ fontSize: 14, color: T.textMuted }}>{row.name}</td>
        <td className="cl-td-muted" style={{ fontSize: 14 }}>{row.email}</td>
        <td style={{ fontSize: 14 }}>{row.phoneNumber}</td>
        <td style={{ fontSize: 14 }}>{row.country || "Malaysia"}</td>
        <td><StatusBadge value={row.status} /></td>
    </tr>
));

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   4. PORTAL-BASED COMPONENTS
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

/** 
 * Dropdown for search types (Email, Phone, etc.) 
 * Uses React Portal to avoid overflow:hidden clipping from the main container.
 */
function SearchTypeDropdown({ anchorRef, open, searchType, onSelect }) {
    const [pos, setPos] = useState({ top: 0, left: 0, width: 160 });

    useEffect(() => {
        if (!open || !anchorRef.current) return;
        const rect = anchorRef.current.getBoundingClientRect();
        setPos({
            top: rect.bottom + window.scrollY + 4,
            left: rect.right + window.scrollX - 160, // Right-aligned
            width: Math.max(rect.width, 160),
        });
    }, [open, anchorRef]);

    if (!open) return null;

    return ReactDOM.createPortal(
        <div
            className="cl-type-dropdown-portal"
            role="listbox"
            aria-label="Search field"
            onMouseDown={(event) => event.stopPropagation()}
            style={{ top: pos.top, left: pos.left, width: pos.width }}
        >
            {ALL_SEARCH_TYPES.map((t) => (
                <button
                    type="button"
                    role="option"
                    aria-selected={searchType === t}
                    key={t}
                    className={`cl-type-dropdown-option${searchType === t ? " selected" : ""}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => onSelect(t)}
                    style={{ display: "block", width: "100%", border: 0, textAlign: "left", font: "inherit" }}
                >
                    {SEARCH_TYPE_LABELS[t]}
                </button>
            ))}
        </div>,
        document.body
    );
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   5. PREVIEW & MODALS
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

const PreviewRow = ({ icon: Icon, label, value }) => {
    if (!value) return null;
    return (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: `1px solid ${T.bg}` }}>
            <div style={{ width: 26, height: 26, borderRadius: "7px", background: T.bg, color: T.textMuted, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon size={12} />
            </div>
            <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 10, color: T.textLight, fontWeight: 500, marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: 13, color: T.textPrimary, fontWeight: 500, wordBreak: "break-word" }}>{value}</div>
            </div>
        </div>
    );
};

function PreviewPanel({ item, type, onClose, onEdit, onDelete, viewOnly, deleteDisabled = false }) {
    if (!item) return null;
    const isCustomer = type === "Customer";

    return (
        <div className="cl-preview-panel">
            <div className="cl-preview-header">
                <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
                    <Avatar name={item.attention} size={44} />
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, letterSpacing: "0.08em", marginBottom: 2 }}>
                            {type.toUpperCase()} PREVIEW
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: T.textPrimary, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {item.attention || "—"}
                        </div>
                        <div style={{ fontSize: 12, color: T.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</div>
                    </div>
                </div>
                <button onClick={onClose} aria-label="Close contact preview" style={{ background: "none", border: "none", cursor: "pointer", color: T.textLight, padding: 4, borderRadius: 4, flexShrink: 0 }}>
                    <FiX size={16} />
                </button>
            </div>

            <div style={{ padding: "10px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 8 }}>
                <StatusBadge value={item.status} />
                <span style={{ fontSize: 11, color: T.textLight }}>{item.id}</span>
            </div>

            <div className="cl-preview-body">
                <PreviewRow icon={FiMail} label="Email" value={item.email} />
                <PreviewRow icon={FiPhone} label="Phone" value={item.phoneNumber} />
                <PreviewRow icon={FiGlobe} label="Country" value={item.country || "Malaysia"} />
                <PreviewRow icon={FiMapPin} label="State / Province" value={item.area} />
                <PreviewRow icon={FiTag} label={isCustomer ? "Incoterms" : "Commodity"} value={isCustomer ? item.incoterms : (() => {
                    try {
                        const list = JSON.parse(item.commodity || "[]");
                        return Array.isArray(list) ? list.join(", ") : item.commodity;
                    } catch {
                        return item.commodity;
                    }
                })()} />
                <PreviewRow icon={FiDollarSign} label="Credit Terms" value={item.creditTerms} />

                <div style={{ marginTop: 12, fontWeight: 600, fontSize: 12, color: "#64748b" }}>Director Contact</div>
                <PreviewRow icon={FiUser} label="Director Name" value={item.directorName} />
                <PreviewRow icon={FiPhone} label="Director Phone" value={item.directorPhone} />
                <PreviewRow icon={FiMail} label="Director Email" value={item.directorEmail} />

                <div style={{ marginTop: 12, fontWeight: 600, fontSize: 12, color: "#64748b" }}>Finance Contact</div>
                <PreviewRow icon={FiUser} label="Finance Name" value={item.financeName} />
                <PreviewRow icon={FiPhone} label="Finance Phone" value={item.financePhone} />
                <PreviewRow icon={FiMail} label="Finance Email" value={item.financeEmail} />

                {item.address && <PreviewRow icon={FiHome} label="Address" value={item.address} />}
                {item.createdAt && <PreviewRow icon={FiCalendar} label="Created" value={new Date(item.createdAt).toLocaleDateString()} />}
            </div>

            <div className="cl-preview-footer">
                <button className="cl-preview-btn-primary" onClick={() => onEdit(item.id)}>{viewOnly ? <FiFileText size={13} /> : <FiEdit3 size={13} />} {viewOnly ? "View" : "View/Edit"}</button>
                {!viewOnly && <button className="cl-preview-btn-secondary" disabled={deleteDisabled} onClick={() => onDelete(item)}><FiTrash2 size={13} /> Delete</button>}
            </div>
        </div>
    );
}

function DeleteModal({ target, onConfirm, onCancel }) {
    if (!target) return null;
    return ReactDOM.createPortal(
        <ModalFrame onCancel={onCancel} labelledBy="contact-delete-modal-title">
                <button className="cl-modal-close" onClick={onCancel} aria-label="Close delete dialog"><FiX size={16} /></button>
                <div className="cl-modal-icon"><FiTrash2 size={28} /></div>
                <p className="cl-modal-msg" id="contact-delete-modal-title">Delete <strong>{target.name}</strong>?</p>
                <div className="cl-modal-warning">
                    <span className="cl-modal-warning-title"><FiAlertTriangle size={14} /> Warning</span>
                    <span className="cl-modal-warning-text">This action is permanent and cannot be undone.</span>
                </div>
                <div className="cl-modal-actions">
                    <button className="cl-modal-btn-cancel" onClick={onCancel}>Cancel</button>
                    <button className="cl-modal-btn-confirm" onClick={onConfirm}>Delete</button>
                </div>
        </ModalFrame>,
        document.body
    );
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   6. MAIN COMPONENT: ContactList
   ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

/**
 * Core list engine for Customers and Suppliers.
 * Handles server-side searching, sorting, and pagination via URL parameters.
 */
export default function ContactList({
    title, type, fetchList, fetchKpis, deleteItem, viewOnly = false, hideCreate = viewOnly,
    STATUSES = ["All", "Active", "Inactive"],
}) {
    // NOTE: every hook below must run on every render, in the same order —
    // so the "not logged in" bailout has to happen after all of them, not
    // before. Returning early above this point would violate the Rules of
    // Hooks and risk React losing track of hook state if this check ever
    // becomes true on one render and false on the next.
    const accountingUser = parseStoredUser(localStorage.getItem("user"));

    const navigate = useNavigate();
    const [sp, setSp] = useSearchParams();
    const searchTypeAnchorRef = useRef(null);
    const queryGenerationRef = useRef(0);
    const deleteInFlightRef = useRef(false);

    // -- State: URL-driven (Derived from search params) --
    const page = sp.get("page") || "1";
    const pageSize = sp.get("pageSize") || "10";
    const sortBy = sp.get("sortBy") || "";
    const sortDir = sp.get("sortDir") || "asc";
    const searchType = sp.get("searchType") || "all";
    const search = sp.get("search") || "";
    const status = sp.get("status") || "All";
    const country = sp.get("country") || "All";

    const queryParams = useMemo(
        () => ({ page, pageSize, sortBy, sortDir, searchType, search, status, country }),
        [page, pageSize, sortBy, sortDir, searchType, search, status, country]
    );

    // -- State: Local UI --
    const [searchInput, setSearchInput] = useState(search);
    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 });
    const [kpis, setKpis] = useState({ total: 0, active: 0, inactive: 0 });
    const [loading, setLoading] = useState(true);
    const [loadingKpi, setLoadingKpi] = useState(true);
    const [error, setError] = useState("");
    const [retryKey, setRetryKey] = useState(0);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteInProgress, setDeleteInProgress] = useState(false);
    const [searchTypeOpen, setSearchTypeOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    const debouncedSearch = useDebounce(searchInput, 350);

    // -- Helpers --
    const setParam = useCallback((key, value) => {
        const next = new URLSearchParams(sp);
        if (!value) next.delete(key); else next.set(key, value);
        if (!["page", "pageSize"].includes(key)) next.set("page", "1");
        setSp(next, { replace: true });
    }, [sp, setSp]);

    const handleSort = useCallback((colId) => {
        const nextDir = sortBy === colId && sortDir === "asc" ? "desc" : "asc";
        const next = new URLSearchParams(sp);
        next.set("sortBy", colId);
        next.set("sortDir", nextDir);
        next.set("page", "1");
        setSp(next, { replace: true });
    }, [sp, setSp, sortBy, sortDir]);

    const resetFilters = () => {
        setSearchInput("");
        setSp(new URLSearchParams({ page: "1", pageSize: "10" }), { replace: true });
    };

    // -- Effects --

    // Effect: Sync debounced search to URL
    useEffect(() => {
        const next = new URLSearchParams(sp);
        if (debouncedSearch) next.set("search", debouncedSearch);
        else next.delete("search");
        next.set("page", "1");
        setSp(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch]);

    // Effect: Keep local input in sync with URL resets
    useEffect(() => { setSearchInput(search); }, [search]);

    // Effect: Data fetching
    useEffect(() => {
        let isMounted = true;
        const requestGeneration = ++queryGenerationRef.current;
        const controller = new AbortController();
        setLoading(true);
        fetchList(queryParams, { signal: controller.signal })
            .then(({ data, pagination: pg }) => {
                if (!isMounted || requestGeneration !== queryGenerationRef.current) return;
                setRows(data);
                setPagination(pg);
                setError("");
                // Re-sync selected item if it was updated
                setSelectedItem((prev) => prev ? (data.find((r) => r.id === prev.id) || null) : null);
            })
            .catch((e) => {
                if (isMounted && e.name !== "AbortError") {
                    setError(e.message || "Failed to load");
                }
            })
            .finally(() => isMounted && setLoading(false));
        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [queryParams, fetchList, retryKey]);

    // Effect: KPI fetching
    useEffect(() => {
        let isMounted = true;
        setLoadingKpi(true);
        fetchKpis()
            .then((data) => isMounted && setKpis(data))
            .finally(() => isMounted && setLoadingKpi(false));
        return () => { isMounted = false; };
    }, [fetchKpis]);

    // Effect: Close dropdown on outside click
    useEffect(() => {
        if (!searchTypeOpen) return;
        const handler = (e) => {
            if (searchTypeAnchorRef.current && !searchTypeAnchorRef.current.contains(e.target))
                setSearchTypeOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [searchTypeOpen]);

    // Effect: Accessibility
    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === "Escape") setSelectedItem(null); };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    // -- Actions --
    const handleDeleteConfirm = async () => {
        if (!deleteTarget || deleteInFlightRef.current) return;
        const previousRows = rows;
        const previousPagination = pagination;
        const previousKpis = kpis;
        const previousSelected = selectedItem;
        const target = deleteTarget;
        const mutationGeneration = queryGenerationRef.current;
        const statusKey = String(target.status || "").toLowerCase();
        deleteInFlightRef.current = true;
        setDeleteInProgress(true);
        setRows((current) => current.filter((row) => row.id !== target.id));
        setPagination((current) => ({
            ...current,
            total: Math.max(0, current.total - 1),
        }));
        setKpis((current) => ({
            ...current,
            total: Math.max(0, (current.total || 0) - 1),
            ...(statusKey === "active"
                ? { active: Math.max(0, (current.active || 0) - 1) }
                : statusKey === "inactive"
                    ? { inactive: Math.max(0, (current.inactive || 0) - 1) }
                    : {}),
        }));
        setDeleteTarget(null);
        if (selectedItem?.id === target.id) setSelectedItem(null);
        try {
            try {
                await deleteItem(target.id);
            } catch (err) {
                if (mutationGeneration === queryGenerationRef.current) {
                    setRows(previousRows);
                    setPagination(previousPagination);
                    setKpis(previousKpis);
                    setSelectedItem(previousSelected);
                    setError("Failed to delete item.");
                }
                return;
            }
            if (mutationGeneration !== queryGenerationRef.current) {
                setRetryKey((current) => current + 1);
                return;
            }
            const nextTotal = Math.max(0, previousPagination.total - 1);
            const lastPage = Math.max(1, Math.ceil(nextTotal / previousPagination.pageSize));
            if (previousPagination.page > lastPage) setParam("page", String(lastPage));
            try {
                const [listResult, newKpis] = await Promise.all([fetchList(queryParams), fetchKpis()]);
                if (mutationGeneration === queryGenerationRef.current) {
                    setRows(listResult.data);
                    setPagination(listResult.pagination);
                    setKpis(newKpis);
                }
            } catch {
                if (mutationGeneration === queryGenerationRef.current) setError("Item deleted, but refresh failed.");
            }
        } finally {
            deleteInFlightRef.current = false;
            setDeleteInProgress(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.pageSize));
    const startIdx = (pagination.page - 1) * pagination.pageSize;
    const basePath = `/contacts/${type.toLowerCase()}s`;

    // All hooks above have run unconditionally — safe to bail out now.
    if (!accountingUser) {
        return <Navigate to="/login" replace />;
    }

    return (
        <div style={{ padding: "20px 0" }} onClick={() => setSearchTypeOpen(false)}>
            {/* 1. Header Section */}
            <div style={{ marginBottom: 18 }}>
                <h4 style={{ fontWeight: 700, color: T.textPrimary, margin: 0 }}>{title}</h4>
                <p style={{ fontSize: 13, color: T.textMuted, margin: "3px 0 0" }}>
                    Manage your {type.toLowerCase()} records. Click a row to preview.
                </p>
            </div>

            {/* 2. KPI Section */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 20 }}>
                <StatCard icon={FiUsers} color="rgb(37,99,235)" label={`Total ${type}s`} value={loadingKpi ? "…" : kpis.total} />
                <StatCard icon={FiUserCheck} color="rgb(5,150,105)" label={`Active ${type}s`} value={loadingKpi ? "…" : kpis.active} />
                <StatCard icon={FiUserX} color="rgb(220,53,69)" label={`Inactive ${type}s`} value={loadingKpi ? "…" : kpis.inactive} />
            </div>

            {/* 2b. Status filter pills */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                {STATUSES.map((s) => (
                    <button
                        key={s}
                        onClick={() => setParam("status", s)}
                        style={{
                            padding: "5px 14px", borderRadius: 999, fontSize: 12, fontWeight: 600,
                            cursor: "pointer", border: "1.5px solid",
                            borderColor: status === s ? T.accent : T.border,
                            background: status === s ? "#eff6ff" : "#fff",
                            color: status === s ? T.accent : T.textMuted,
                            transition: "all 0.15s",
                        }}
                    >
                        {s}
                    </button>
                ))}
            </div>

            {/* 3. Content Section */}
            <div className="cl-content-wrapper">

                {/* List Container */}
                <div className="cl-table-container" style={{
                    flex: 1, minWidth: 0, background: T.cardBg,
                    borderRadius: T.radius, border: `1px solid ${T.border}`,
                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                }}>

                    {/* Toolbar (Sticky) */}
                    <div className="cl-toolbar-sticky">
                        <div className="cl-search-bar" style={{ flex: 1, minWidth: 220, maxWidth: 440 }}>
                            <FiSearch size={14} style={{ margin: "auto 8px auto 10px", color: T.textLight, flexShrink: 0 }} />
                            <input
                                className="cl-search-input"
                                type="text"
                                placeholder={`Search by ${SEARCH_TYPE_LABELS[searchType] || "all"}…`}
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => e.key === "Escape" && (setSearchInput(""), setParam("search", ""))}
                                aria-label="Search"
                            />
                            <div className="cl-search-divider" />

                            {/* Options Dropdown */}
                            <div
                                className="cl-search-type-wrap"
                                ref={searchTypeAnchorRef}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <button
                                    type="button"
                                    className="cl-search-type-btn"
                                    onClick={() => setSearchTypeOpen((o) => !o)}
                                >
                                    {SEARCH_TYPE_LABELS[searchType] || "All"}
                                </button>
                                <FiChevronDown size={13} className={`cl-search-chevron${searchTypeOpen ? " open" : ""}`} />

                                <SearchTypeDropdown
                                    anchorRef={searchTypeAnchorRef}
                                    open={searchTypeOpen}
                                    searchType={searchType}
                                    onSelect={(t) => { setParam("searchType", t); setSearchTypeOpen(false); }}
                                />
                            </div>
                        </div>

                        <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
                            <button className="cl-btn-reset" onClick={resetFilters}><FiRefreshCw size={13} /> Reset Filter</button>
                            {!hideCreate && !viewOnly && <button className="cl-btn-add" onClick={() => navigate(`${basePath}/add`)}><FiPlus size={14} /> Add {type}</button>}
                        </div>
                    </div>

                    {/* Table View */}
                    <div className="cl-table-box">
                        <div className="cl-table-scroll">
                            <table className="cl-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 40 }}><FiHash size={11} /></th>
                                        <SortTh label={`${type} ID`} id="ID" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                        <SortTh label="Contact Person" id="Contact" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                        <SortTh label="Company Name" id="Name" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                        <th>Email</th>
                                        <th>Phone</th>
                                        <SortTh label="Country" id="Country" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                        <SortTh label="Status" id="Status" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                                    </tr>
                                </thead>
                                <tbody style={{ opacity: loading && rows.length ? 0.55 : 1 }}>
                                    {loading && rows.length === 0 && <tr><td colSpan={8} className="cl-empty"><Spinner size="sm" /> &nbsp;Loading…</td></tr>}
                                    {!loading && error && <tr><td colSpan={8} className="cl-empty" style={{ color: T.danger }}>
                                        {error}{" "}
                                        <button type="button" onClick={() => setRetryKey((key) => key + 1)}>Retry</button>
                                    </td></tr>}
                                    {!loading && !error && rows.length === 0 && <tr><td colSpan={8} className="cl-empty">No {type.toLowerCase()}s found.</td></tr>}
                                    {!error && rows.map((r, idx) => (
                                        <ContactRow key={r.id} row={r} index={startIdx + idx} isSelected={selectedItem?.id === r.id} onRowClick={setSelectedItem} />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination Bar */}
                    <div className="cl-pagination" onClick={(e) => e.stopPropagation()}>
                        <div className="cl-rows-label">
                            Rows per page:
                            <select className="cl-rows-select" value={pageSize} onChange={(e) => setParam("pageSize", e.target.value)}>
                                {["5", "10", "20", "50"].map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </div>
                        <span className="cl-page-info">
                            {pagination.total === 0 ? "0–0 of 0" : `${startIdx + 1}–${Math.min(startIdx + pagination.pageSize, pagination.total)} of ${pagination.total}`}
                        </span>
                        <div style={{ display: "flex", gap: 5 }}>
                            <button className="cl-page-btn" onClick={() => setParam("page", "1")} disabled={pagination.page <= 1} title="First" aria-label="First page"><FiChevronsLeft size={15} /></button>
                            <button className="cl-page-btn" onClick={() => setParam("page", String(pagination.page - 1))} disabled={pagination.page <= 1} title="Previous" aria-label="Previous page"><FiChevronLeft size={15} /></button>
                            <button className="cl-page-btn" onClick={() => setParam("page", String(pagination.page + 1))} disabled={pagination.page >= totalPages} title="Next" aria-label="Next page"><FiChevronRight size={15} /></button>
                            <button className="cl-page-btn" onClick={() => setParam("page", String(totalPages))} disabled={pagination.page >= totalPages} title="Last" aria-label="Last page"><FiChevronsRight size={15} /></button>
                        </div>
                    </div>
                </div>

                {/* Preview Panel View */}
                {selectedItem && (
                    <PreviewPanel
                        item={selectedItem}
                        type={type}
                        onClose={() => setSelectedItem(null)}
                        onEdit={(id) => navigate(`${basePath}/${id}${viewOnly ? "" : "?mode=edit"}`)}
                        onDelete={(item) => setDeleteTarget(item)}
                        viewOnly={viewOnly}
                        deleteDisabled={deleteInProgress}
                    />
                )}
            </div>

            {/* Modals & Portals */}
            <DeleteModal
                target={deleteTarget}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteTarget(null)}
            />
        </div>
    );
}